// src/components/auction/StrategyManage.js
import React, { useEffect, useMemo, useState } from 'react';
import './StrategyManage.css';
import {
  getUserStrategies,
  getPublicStrategies,
  getStrategyById,
  getStrategiesSharedWithMe,
} from '../../firebase/strategyService';
import {
  X,
  NotebookPen,
  Tag,
  Award,
  StickyNote,
  Loader2,
  AlertTriangle,
  Download,
  ArrowLeft,
  Search,
  Lock,
  Globe2,
  Users,
} from 'lucide-react';

function countKeys(obj) {
  return Object.keys(obj || {}).length;
}

function normalizeText(value = '') {
  return String(value)
    .toLowerCase()
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

export default function StrategyManage({ open, uid, onClose, onImport }) {
  const [tab, setTab] = useState('mine');
  const [search, setSearch] = useState('');
  const [myStrategies, setMyStrategies] = useState([]);
  const [publicStrategies, setPublicStrategies] = useState([]);
  const [sharedStrategies, setSharedStrategies] = useState([]);
  const [loading, setLoading] = useState(false);

  const [confirming, setConfirming] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !uid) return;
    setTab('mine');
    setSearch('');
    setConfirming(null);
    setError('');
    setLoading(true);
    Promise.all([getUserStrategies(uid), getPublicStrategies(), getStrategiesSharedWithMe(uid)])
      .then(([mine, pub, shared]) => {
        setMyStrategies(mine);
        setPublicStrategies((pub || []).filter((s) => s.ownerUid !== uid));
        setSharedStrategies(shared || []);
      })
      .catch(() => {
        setMyStrategies([]);
        setPublicStrategies([]);
        setSharedStrategies([]);
      })
      .finally(() => setLoading(false));
  }, [open, uid]);

  const list = tab === 'mine' ? myStrategies : tab === 'shared' ? sharedStrategies : publicStrategies;
  const filtered = useMemo(() => {
    const q = normalizeText(search);
    if (!q) return list;
    return list.filter(
      (s) => normalizeText(s.name || '').includes(q) || normalizeText(s.description || '').includes(q)
    );
  }, [list, search]);

  if (!open) return null;

  const handlePick = async (item) => {
    setError('');
    if (tab === 'mine') {
      setConfirming(item);
      return;
    }
    // Le voci "pubbliche" e "condivise con me" sono registri leggeri (solo
    // nome/descrizione): al momento della scelta va letto il documento sorgente
    // aggiornato (etichette/fasce/note reali, sempre corrente), reso leggibile
    // dalla regola dedicata — visibility === 'public' oppure il proprio uid in
    // `sharedWith`.
    setConfirmLoading(true);
    try {
      const full = await getStrategyById(item.ownerUid, item.strategyId || item.id);
      const stillAvailable = full && (
        tab === 'shared'
          ? (Array.isArray(full.sharedWith) && full.sharedWith.map(String).includes(String(uid)))
          : full.visibility === 'public'
      );
      if (!stillAvailable) {
        setError('Questa strategia non è più disponibile.');
        return;
      }
      setConfirming(full);
    } catch {
      setError('Impossibile leggere questa strategia. Riprova.');
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!confirming) return;
    setImporting(true);
    setError('');
    try {
      await onImport(confirming);
      onClose();
    } catch {
      setError('Import non riuscito. Riprova.');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="sman-backdrop" onClick={onClose}>
      <div className="sman-modal glass" onClick={(e) => e.stopPropagation()}>
        <div className="sman-head">
          <div>
            <div className="sman-eyebrow">
              <NotebookPen size={14} />
              Importa strategia
            </div>
            <h2>{confirming ? confirming.name : 'Scegli una strategia'}</h2>
            <p>
              {confirming
                ? 'Le etichette, fasce e note della strategia sovrascriveranno quelle già presenti per i giocatori coperti. I giocatori non coperti restano invariati.'
                : 'Importa un preset creato dal menu Strategie: verrà applicato solo a questa asta.'}
            </p>
          </div>
          <button className="sman-close" onClick={onClose} type="button"><X size={18} /></button>
        </div>

        {confirming ? (
          <div className="sman-confirm">
            <div className="sman-confirm__stats">
              <span><Tag size={13} /> {countKeys(confirming.categories)} etichette</span>
              <span><Award size={13} /> {countKeys(confirming.tiers)} fasce</span>
              <span><StickyNote size={13} /> {countKeys(confirming.notes)} note</span>
            </div>

            {error && (
              <div className="sman-error"><AlertTriangle size={14} />{error}</div>
            )}

            <div className="sman-confirm__actions">
              <button type="button" className="sman-back-btn" onClick={() => setConfirming(null)} disabled={importing}>
                <ArrowLeft size={14} />
                Indietro
              </button>
              <button type="button" className="sman-import-btn" onClick={handleConfirm} disabled={importing}>
                {importing ? <><Loader2 size={15} className="spin" /> Importo...</> : <><Download size={15} /> Conferma import</>}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="sman-tabs">
              <button type="button" className={`sman-tab ${tab === 'mine' ? 'is-active' : ''}`} onClick={() => setTab('mine')}>
                <Lock size={13} /> Le mie ({myStrategies.length})
              </button>
              <button type="button" className={`sman-tab ${tab === 'shared' ? 'is-active' : ''}`} onClick={() => setTab('shared')}>
                <Users size={13} /> Condivise ({sharedStrategies.length})
              </button>
              <button type="button" className={`sman-tab ${tab === 'public' ? 'is-active' : ''}`} onClick={() => setTab('public')}>
                <Globe2 size={13} /> Pubbliche ({publicStrategies.length})
              </button>
            </div>

            <label className="sman-search">
              <Search size={14} />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cerca per nome o descrizione…"
              />
            </label>

            {error && <div className="sman-error"><AlertTriangle size={14} />{error}</div>}

            {loading || confirmLoading ? (
              <div className="sman-loading"><Loader2 size={18} className="spin" /> {confirmLoading ? 'Apro strategia...' : 'Caricamento strategie...'}</div>
            ) : filtered.length === 0 ? (
              <div className="sman-empty">
                {tab === 'mine'
                  ? 'Non hai ancora nessuna strategia salvata. Creane una dal menu "Strategie" in Home.'
                  : tab === 'shared'
                    ? 'Nessuna strategia condivisa con te. Dai il tuo codice a un amico dal menu "Strategie".'
                    : 'Nessuna strategia pubblica trovata al momento.'}
              </div>
            ) : (
              <div className="sman-list">
                {filtered.map((strategy) => (
                  <button key={strategy.id} type="button" className="sman-item" onClick={() => handlePick(strategy)}>
                    <div className="sman-item__icon"><NotebookPen size={16} /></div>
                    <div className="sman-item__body">
                      <strong>{strategy.name}</strong>
                      {strategy.description && <span className="sman-item__desc">{strategy.description}</span>}
                      {tab === 'mine' && (
                        <div className="sman-item__meta">
                          <span><Tag size={11} /> {countKeys(strategy.categories)}</span>
                          <span><Award size={11} /> {countKeys(strategy.tiers)}</span>
                          <span><StickyNote size={11} /> {countKeys(strategy.notes)}</span>
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
